// Range select and channel toggle pills at the top of the home dashboard.

import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import {
  CHANNEL_OPTIONS,
  RANGE_OPTIONS,
  partialRangeNote,
  toggleChannel,
  useHomeRange,
  type HomeFilters,
  type RangeKey,
} from '@/lib/useHomeDashboard';
import { MUTED_TEXT } from './tileText';

type Props = { filters: HomeFilters; onChange: (next: HomeFilters) => void };

export function HomeFilterBar({ filters, onChange }: Props) {
  const { data: range } = useHomeRange(filters);
  const onlyOne = filters.channels.length === 1;

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-amp-white px-[18px] py-3 shadow-1 dark:bg-amp-dark-surface lg:flex-row lg:items-end lg:gap-6">
      <div>
        <FilterLabel htmlFor="home-range">Date range</FilterLabel>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <select
            id="home-range"
            value={filters.range}
            onChange={(e) => onChange({ ...filters, range: e.target.value as RangeKey })}
            className="rounded border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            {RANGE_OPTIONS.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
          <span className={`text-[13px] ${MUTED_TEXT}`}>
            {range ? `${range.label}, vs. same days last year` : 'vs. same days last year'}
          </span>
          {partialRangeNote(range) && (
            <span className={`text-[13px] ${MUTED_TEXT}`}>{partialRangeNote(range)}</span>
          )}
        </div>
      </div>
      <div>
        <FilterLabel as="div">Channels</FilterLabel>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Sales channels">
          {CHANNEL_OPTIONS.map((c) => {
            const on = filters.channels.includes(c.key);
            return (
              <Pill
                key={c.key}
                variant={on ? 'selected' : 'outline'}
                aria-pressed={on}
                title={on && onlyOne ? 'At least one channel stays on' : undefined}
                onClick={() => onChange(toggleChannel(filters, c.key))}
              >
                {c.label}
              </Pill>
            );
          })}
        </div>
      </div>
    </div>
  );
}
